import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usePlanningStore } from '@/stores/planning';
import { useAuthStore } from '@/stores/auth';
import { usePermissionsStore } from '@/stores/permissions';
import { FaseInterventieCheckbox } from '@/components/project/FaseInterventieCheckbox';

export function CellContextMenu() {
  const cellContextMenu = usePlanningStore((s) => s.cellContextMenu);
  const closeCellContextMenu = usePlanningStore((s) => s.closeCellContextMenu);
  const enterCreateMode = usePlanningStore((s) => s.enterCreateMode);
  const executeCreateTask = usePlanningStore((s) => s.executeCreateTask);
  const exitCreateMode = usePlanningStore((s) => s.exitCreateMode);
  const setShowProjectSearch = usePlanningStore((s) => s.setShowProjectSearch);
  const isAdministratie = useAuthStore((s) => s.hasRole('administratie'));
  const canPlanningEdit = usePermissionsStore((s) => s.canFeature('planning_edit'));

  const [nameInput, setNameInput] = useState('');
  const [nameInputType, setNameInputType] = useState<'feestdag' | 'vrije_taak' | null>(null);
  const [interventie, setInterventie] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const close = useCallback(() => {
    closeCellContextMenu();
    setNameInputType(null);
    setNameInput('');
    setInterventie(false);
  }, [closeCellContextMenu]);

  useEffect(() => {
    if (!cellContextMenu) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        close();
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (nameInputType) { setNameInputType(null); setNameInput(''); return; }
        close();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [cellContextMenu, close, nameInputType]);

  useEffect(() => {
    if (nameInputType && inputRef.current) inputRef.current.focus();
  }, [nameInputType]);

  if (!cellContextMenu) return null;
  // Read-only viewers (e.g. projectleider_easykit) get no right-click menu.
  if (!canPlanningEdit && !isAdministratie) return null;

  const { dateKey, installerId, x, y } = cellContextMenu;

  const flipUp = y > window.innerHeight - 220;
  const flipLeft = x > window.innerWidth - 240;

  function handleVerlof() {
    enterCreateMode('verlof');
    executeCreateTask(dateKey, installerId);
    exitCreateMode();
    close();
  }

  function handleFeestdag() {
    setNameInputType('feestdag');
    setNameInput('Feestdag');
  }

  function handleVrijeTaak() {
    setNameInputType('vrije_taak');
    setNameInput('');
    setInterventie(false);
  }

  function handleInplannen() {
    usePlanningStore.setState({ scheduleTargetCell: { dateKey, installerId } });
    setShowProjectSearch(true);
    closeCellContextMenu();
    setNameInputType(null);
    setNameInput('');
  }

  function submitName() {
    if (!nameInput.trim() || !nameInputType) return;
    enterCreateMode(nameInputType, nameInput.trim(), nameInputType === 'vrije_taak' && interventie);
    executeCreateTask(dateKey, installerId);
    close();
  }

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-50 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-1"
      style={{
        left: flipLeft ? x - 224 : x,
        top: flipUp ? undefined : y,
        bottom: flipUp ? window.innerHeight - y : undefined,
      }}
    >
      {!nameInputType ? (
        <>
          {!isAdministratie && (
            <>
              <button type="button" onClick={handleInplannen} className="w-full text-left px-4 py-2.5 text-sm text-td-navy hover:bg-gray-50 transition-colors">
                Project inplannen
              </button>
              <button type="button" onClick={handleVrijeTaak} className="w-full text-left px-4 py-2.5 text-sm text-td-navy hover:bg-gray-50 transition-colors">
                Project aanmaken
              </button>
            </>
          )}
          <button type="button" onClick={handleFeestdag} className="w-full text-left px-4 py-2.5 text-sm text-td-navy hover:bg-gray-50 transition-colors">
            Feestdag
          </button>
          <button type="button" onClick={handleVerlof} className="w-full text-left px-4 py-2.5 text-sm text-td-navy hover:bg-gray-50 transition-colors">
            Verlof
          </button>
        </>
      ) : (
        <>
        <div className="px-4 py-3 flex items-center gap-1.5">
          <label htmlFor="cell-context-name-input" className="sr-only">Naam</label>
          <input
            ref={inputRef}
            id="cell-context-name-input"
            type="text"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitName();
            }}
            placeholder={nameInputType === 'feestdag' ? 'Feestdag' : 'Taaknaam...'}
            className="flex-1 border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs"
          />
          <button type="button" onClick={submitName} disabled={!nameInput.trim()} className="px-2.5 py-1.5 text-xs font-semibold text-white bg-td-blue rounded-lg hover:bg-td-blue-dark transition-colors disabled:opacity-50">
            OK
          </button>
        </div>
        {nameInputType === 'vrije_taak' && (
          <div className="px-4 pb-3">
            <FaseInterventieCheckbox checked={interventie} onChange={setInterventie} />
          </div>
        )}
        </>
      )}
    </div>,
    document.body,
  );
}
